import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { addToast, cn } from '@heroui/react';

import ButtonV2 from '../Button';
import Typography from '../Typography';
import {
  escapeOrderedListMarkers,
  ensureBlankLineBeforeThematicBreak,
  linkifyPlainUrls,
  unescapeMarkdownLinks
} from '../../../utils/markdownUtils';

// ---------------------------------------------------------------------------
// Inlined pure utilities (no external deps)
// ---------------------------------------------------------------------------

/** Strips `auth_token` from same-origin URLs, returns path+search for internal navigation. */
function processInternalUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    if (urlObj.origin === window.location.origin) {
      const searchParams = new URLSearchParams(urlObj.search);
      searchParams.delete('auth_token');
      const newSearch = searchParams.toString();
      return `${urlObj.pathname}${newSearch ? `?${newSearch}` : ''}`;
    }
  } catch {
    return url;
  }
  return url;
}

/** Converts heading text to a URL-friendly slug for anchor IDs. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

// ---------------------------------------------------------------------------
// Renderer sub-components (defined at module level for referential stability)
// ---------------------------------------------------------------------------

const QuoteMarkdown = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <Typography as="div" className="block" {...(props as any)}>
    {props.children}
  </Typography>
);

const TableMarkdown = (props: React.HTMLAttributes<HTMLTableElement>) => (
  <div className="w-full overflow-x-auto my-4 max-w-full">
    <table className="border-collapse border border-gray-4 min-w-max">
      {props.children}
    </table>
  </div>
);

const TableHeadMarkdown = (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <thead className="bg-gray-2">{props.children}</thead>
);

const TableBodyMarkdown = (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody>{props.children}</tbody>
);

const TableRowMarkdown = (props: React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr className="border-b border-gray-4">{props.children}</tr>
);

const TableCellMarkdown = ({ isHeader, ...props }: any) =>
  isHeader ? (
    <th className="p-3 text-left border-b border-gray-4">
      <Typography className="whitespace-nowrap" variant="body-2" weight="bold">
        {props.children}
      </Typography>
    </th>
  ) : (
    <td className="p-3 border-b border-gray-4">
      <Typography className="whitespace-nowrap" variant="body-2">
        {props.children}
      </Typography>
    </td>
  );

const ListMarkdown = (props: React.HTMLAttributes<HTMLUListElement>) => (
  <ul className="list-disc pl-5 mb-2 last:mb-0">{props.children}</ul>
);

const NumberListMarkdown = ({ children, ...props }: any) => (
  <ol {...props} className="list-decimal pl-5 mb-2 last:mb-0">
    {children}
  </ol>
);

const ParagraphMarkdown = ({ className, ...props }: any) => (
  <Typography
    variant="body-1"
    as="p"
    {...props}
    className={cn('mb-2 last:mb-0', className)}
  >
    {props.children}
  </Typography>
);

const BlockquoteMarkdown = (props: React.HTMLAttributes<HTMLQuoteElement>) => (
  <blockquote className="border-l-4 border-gray-4 pl-3 my-2 text-gray-600 italic">
    {props.children}
  </blockquote>
);

const InlineCodeMarkdown = (props: React.HTMLAttributes<HTMLElement>) => (
  <code className="bg-gray-100 font-mono text-[0.875em] px-1 py-0.5 rounded">
    {props.children}
  </code>
);

const PreMarkdown = (props: React.HTMLAttributes<HTMLPreElement>) => (
  <pre className="bg-gray-100 border border-gray-4 rounded-md p-3 my-2 overflow-x-auto font-mono text-sm leading-relaxed">
    {props.children}
  </pre>
);

const LineMarkdown = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div className="my-2" {...props}>
    {props.children}
  </div>
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RemarkPluginList = NonNullable<
  React.ComponentProps<typeof ReactMarkdown>['remarkPlugins']
>;

export interface MarkdownContentProps {
  markdownString: string;
  replace?: Record<string, string>;
  className?: string;
  wrapperClassName?: string;
  hideImages?: boolean;
  /** Injected by the app shim via useNavigate() — keeps the package router-agnostic. */
  navigate?: (to: string) => void;
  /** Opens every link in a new tab, internal ones included, instead of navigating in place. */
  openLinksInNewTab?: boolean;
  /** Render prop for the image gallery — each app shim injects its own ImageGallery. */
  renderImages?: (imageUrls: { url: string }[]) => React.ReactNode;
  /** Shows a copy-to-clipboard button (ops feature). */
  isCopyable?: boolean;
  /** Styles applied to inline <img> nodes. When set, images are kept inline; when absent, images are stripped from the body and only shown via renderImages. */
  imageStyles?: React.CSSProperties;
  /** Generates id attributes on heading elements via slugify (ops feature). */
  computeHeadingIds?: boolean;
  /** Additional remark plugins prepended to [remarkBreaks, remarkGfm] (e.g. remarkMentions in ops). */
  extraRemarkPlugins?: RemarkPluginList;
  /** Called for each detected signal tag (<end>, <completion X>) — frontend streaming feature. */
  onSignalDetected?: (signal: string) => void;
  /** Rendered as a "Review RFQ" CTA when an <end> signal is detected — frontend streaming feature. */
  onDone?: () => void;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Shared markdown renderer used by both frontend and frontend-ops.
 * App-specific behavior (navigation, image gallery, remark plugins, signal
 * detection) is injected via props — each app provides a thin shim that
 * pre-configures the defaults so no callers need to change.
 */
const MarkdownContent: React.FC<MarkdownContentProps> = ({
  markdownString,
  replace,
  className,
  wrapperClassName = '',
  hideImages = false,
  navigate,
  openLinksInNewTab = false,
  renderImages,
  isCopyable = false,
  imageStyles,
  computeHeadingIds = false,
  extraRemarkPlugins,
  onSignalDetected,
  onDone
}) => {
  // -------------------------------------------------------------------------
  // Text processing pipeline
  // -------------------------------------------------------------------------
  const shouldDetectSignals = !!onSignalDetected || !!onDone;

  const { content, imageUrls, detectedSignals } = useMemo(() => {
    let text = markdownString ?? '';

    // 1. Variable substitution
    if (replace) {
      text = text.replace(
        /\{\{(\w+)\}\}/g,
        (match, key) => replace[key] || match
      );
    }

    // 2. Signal detection — strip tags and collect signals
    const signals: string[] = [];
    if (shouldDetectSignals) {
      text = text.replace(/<end>/g, () => {
        signals.push('end');
        return '';
      });
      text = text.replace(/<completion ([\d.]+)>/g, (_, n: string) => {
        signals.push(`completion ${n}`);
        return '';
      });
    }

    // 3. Image URL extraction
    const urls: { url: string }[] = [];
    if (imageStyles !== undefined) {
      // Keep images inline (ops behavior) — extract URLs without stripping
      text.replace(/!\[.*?\]\((.*?)\)/g, (_, url: string) => {
        urls.push({ url });
        return _;
      });
    } else {
      // Strip images from body (frontend behavior) — they appear only in renderImages
      text = text.replace(/!\[.*?\]\((.*?)\)/g, (_, url: string) => {
        urls.push({ url });
        return '';
      });
    }

    // 4. Plain URL → markdown link
    text = linkifyPlainUrls(text);

    // 5. Markdown normalization
    text = escapeOrderedListMarkers(
      ensureBlankLineBeforeThematicBreak(unescapeMarkdownLinks(text))
    );

    // 6. Strip trailing newline
    text = text.replace(/\n$/, '');

    return { content: text, imageUrls: urls, detectedSignals: signals };
  }, [markdownString, replace, imageStyles, shouldDetectSignals]);

  // Fire signal callbacks after render
  useEffect(() => {
    if (onSignalDetected && detectedSignals.length > 0) {
      detectedSignals.forEach((signal) => onSignalDetected(signal));
    }
  }, [detectedSignals, onSignalDetected]);

  // -------------------------------------------------------------------------
  // ReactMarkdown component map — stable via useMemo
  // -------------------------------------------------------------------------
  const components = useMemo(() => {
    const makeHeading =
      (variant: string) =>
      (props: any): React.ReactElement => {
        const text = props?.children ?? '';
        const id = computeHeadingIds
          ? slugify(typeof text === 'string' ? text : '')
          : undefined;
        return (
          <Typography
            variant={variant as any}
            className="block mb-2"
            weight="bold"
            id={id}
            {...props}
          >
            {props.children}
          </Typography>
        );
      };

    const LinkMarkdownRenderer = (props: any) => {
      const processedHref = processInternalUrl(props.href ?? '');

      const handleLink = () => {
        // A leading '?' is a query-only link (e.g. `?sample=<id>`, which opens a
        // drawer): the router resolves it against the page the user is on.
        const isInternal =
          processedHref?.startsWith('/') || processedHref?.startsWith('?');
        if (isInternal && navigate && !openLinksInNewTab) {
          navigate(processedHref);
        } else if (processedHref) {
          window.open(processedHref, '_blank');
        }
      };

      return (
        <Typography
          as="div"
          className="cursor-pointer text-primary underline inline-flex break-all"
          onClick={handleLink}
          {...props}
        >
          {props.children}
        </Typography>
      );
    };

    const ListItemMarkdownRenderer = (props: any) => (
      <Typography as="li" variant="body-1">
        {props.children}
      </Typography>
    );

    return {
      p: (props: any) => (
        <ParagraphMarkdown {...props} className={className} />
      ),
      div: QuoteMarkdown,
      a: LinkMarkdownRenderer,
      ul: ListMarkdown,
      li: ListItemMarkdownRenderer,
      ol: NumberListMarkdown,
      h1: makeHeading('h4'),
      h2: makeHeading('h5'),
      h3: makeHeading('h7'),
      h4: makeHeading('body-1'),
      h5: makeHeading('body-2'),
      h6: makeHeading('body-3'),
      blockquote: BlockquoteMarkdown,
      pre: PreMarkdown,
      code: InlineCodeMarkdown,
      hr: LineMarkdown,
      table: TableMarkdown,
      thead: TableHeadMarkdown,
      tbody: TableBodyMarkdown,
      tr: TableRowMarkdown,
      td: (props: any) => <TableCellMarkdown {...props} />,
      th: (props: any) => <TableCellMarkdown isHeader {...props} />,
      ...(imageStyles !== undefined
        ? {
            img: (props: any) => (
              <img
                alt={props?.alt}
                src={props?.src}
                style={imageStyles || {}}
              />
            )
          }
        : {})
    };
  }, [computeHeadingIds, imageStyles, navigate, openLinksInNewTab, className]);

  // -------------------------------------------------------------------------
  // Remark plugin list — stable via useMemo
  // -------------------------------------------------------------------------
  const remarkPlugins = useMemo(
    () =>
      [
        remarkBreaks,
        remarkGfm,
        ...(extraRemarkPlugins ?? [])
      ] as RemarkPluginList,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraRemarkPlugins]
  );

  // -------------------------------------------------------------------------
  // Clipboard copy handler
  // -------------------------------------------------------------------------
  const handleCopy = () => {
    navigator.clipboard.writeText(markdownString);
    addToast({ title: 'Copied to clipboard' });
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div
      className={cn(
        'flex flex-col break-word text-left w-full',
        isCopyable && 'group relative',
        className
      )}
    >
      {isCopyable && (
        <div className="sticky top-2 z-10 flex w-fit right-0 ml-auto">
          <button
            className="p-2 border border-gray-4 rounded-md bg-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-gray-3"
            title="Copy to clipboard"
            onClick={handleCopy}
          >
            <DocumentDuplicateIcon className="h-3 w-3" />
          </button>
        </div>
      )}
      <div
        className={cn(
          'relative w-full',
          isCopyable && '-mt-8',
          wrapperClassName
        )}
      >
        <ReactMarkdown
          components={components as React.ComponentProps<typeof ReactMarkdown>['components']}
          remarkPlugins={remarkPlugins}
        >
          {content}
        </ReactMarkdown>

        {!hideImages && renderImages && renderImages(imageUrls)}

        {detectedSignals.includes('end') && onDone && (
          <div className="my-4">
            <ButtonV2
              onPress={onDone}
              size="lg"
              endContent={
                <ArrowRightIcon className="size-4 pointer-events-none" />
              }
              className="h-14 rounded-full px-6"
            >
              Review RFQ
            </ButtonV2>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkdownContent;
